import React, {
  createContext,
  FunctionComponent,
  ReactNode,
  useState,
} from "react";
import { IApplicationListRowModel } from "./applications-model";

const rowsPerPageOptions = [10, 20, 50, 100, 500];
const defaultRowsPerPage = rowsPerPageOptions[0];

interface IDefaultValues {
  page: number;
  searchTerm: string;
  rowsPerPage: number;
}

export enum ListAppTabs {
  All = 0,
  NotActivated,
  ExpiredActivation,
}

const defaultApplication: IApplicationListRowModel = {
  count: 0,
  devTenantApps: [],
};

interface IApplicationsContext {
  additionalDevices: number | undefined;
  applicationDefinition: number | undefined;
  appListTab: number;
  applications: IApplicationListRowModel;
  appsLoaded: boolean;
  defaults: IDefaultValues;
  editAppTab: number;
  isAppListLoading: boolean;
  ListAppTabs: typeof ListAppTabs;
  onEditValidation: any;
  originalAppName: string | null;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  searchTerm: string;
  selectedApp: string | null;
  selectedCheckedApps: string[];
  showSettingTab: boolean;
  urlLoaded: boolean;
  setAdditionalDevices: React.Dispatch<React.SetStateAction<number | undefined>>;
  setApplicationDefinition: React.Dispatch<React.SetStateAction<number | undefined>>;
  setAppListTab: React.Dispatch<React.SetStateAction<number>>;
  setApplications: React.Dispatch<React.SetStateAction<IApplicationListRowModel>>;
  setAppsLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setEditAppTab: React.Dispatch<React.SetStateAction<number>>;
  setIsAppListLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setOnEditValidation: React.Dispatch<React.SetStateAction<any>>;
  setOriginalAppName: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setSelectedApp: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedCheckedApps: React.Dispatch<React.SetStateAction<string[]>>;
  setShowSettingTab: React.Dispatch<React.SetStateAction<boolean>>;
  setUrlLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ApplicationsProviderProps {
  children: ReactNode;
}

export const ApplicationsContext = createContext<IApplicationsContext>(
  {} as IApplicationsContext
);

export const ApplicationsProvider: FunctionComponent<ApplicationsProviderProps> = ({ children }) => {
  const [page, setPage] = useState(0);
  const [selectedApp, setSelectedApp] = useState<string | null>(null);
  const [selectedCheckedApps, setSelectedCheckedApps] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editAppTab, setEditAppTab] = useState(1);
  const [isAppListLoading, setIsAppListLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [urlLoaded, setUrlLoaded] = useState(false);
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [onEditValidation, setOnEditValidation] = useState<any>({});
  const [appListTab, setAppListTab] = useState(ListAppTabs.All);
  const [showSettingTab, setShowSettingTab] = useState(false);
  const [additionalDevices, setAdditionalDevices] = useState<number>();
  const [applicationDefinition, setApplicationDefinition] = useState<number>();
  const [originalAppName, setOriginalAppName] = useState<string | null>(null);
  const [applications, setApplications] = useState<IApplicationListRowModel>(defaultApplication);

  return (
    <ApplicationsContext.Provider
      value={{
        additionalDevices,
        applicationDefinition,
        appListTab,
        applications,
        appsLoaded,
        defaults: {
          page: 0,
          searchTerm: "",
          rowsPerPage: defaultRowsPerPage,
        },
        editAppTab,
        isAppListLoading,
        ListAppTabs,
        onEditValidation,
        originalAppName,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        searchTerm,
        selectedApp,
        selectedCheckedApps,
        showSettingTab,
        urlLoaded,
        setAdditionalDevices,
        setApplicationDefinition,
        setAppListTab,
        setApplications,
        setAppsLoaded,
        setEditAppTab,
        setIsAppListLoading,
        setOnEditValidation,
        setOriginalAppName,
        setPage,
        setRowsPerPage,
        setSearchTerm,
        setSelectedApp,
        setSelectedCheckedApps,
        setShowSettingTab,
        setUrlLoaded,
      }}
    >
      {children}
    </ApplicationsContext.Provider>
  );
};

export const ApplicationsConsumer = ApplicationsContext.Consumer;
